import { Dialog } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';

import { useRecordingAnalytics } from '../../../hooks';
import abstractBlurredLight from '../assets/abstract-blurred-light.jpg';
import editorUiPreview from '../assets/editor-ui-preview.jpg';

type RecordingDownloadDialogProps = {
  isOpen: boolean;
  onDownload: () => void;
  onGoToCreatorLab: () => void;
};

export const RecordingDownloadDialog = ({
  isOpen,
  onDownload,
  onGoToCreatorLab,
}: RecordingDownloadDialogProps) => {
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();

  // Prevent dialog from closing via ESC or backdrop
  const handleClose = () => {
    // No-op: dialog can only be closed via button actions
  };

  const handleDownload = () => {
    trackRecordingEvent('creator_lab_download_anyway_clicked');
    onDownload();
  };

  const handleGoToCreatorLab = () => {
    trackRecordingEvent('creator_lab_go_to_clicked');
    onGoToCreatorLab();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-[999]">
      <div className="fixed inset-0 overflow-y-auto bg-gray-800/60">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="relative w-[37.5rem] overflow-hidden rounded-[.625rem] bg-spring-neutral-base pt-6 pb-5 shadow-[0px_10px_20px_-10px_rgba(0,0,0,0.2)]">
            <div className="px-6 pb-3">
              <Dialog.Title
                className="font-inter text-[1.0625rem] font-medium leading-[1.5625rem] tracking-[-0.2px] text-spring-neutral-b0"
                aria-label={t('content-recording.download-dialog.title')}
              >
                {t('content-recording.download-dialog.title')}
              </Dialog.Title>
            </div>

            <div className="px-5 py-4">
              <div className="relative h-[13.375rem] w-full overflow-hidden">
                <img
                  src={abstractBlurredLight}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute left-[37px] top-[24px] h-[190px] w-[489px] overflow-hidden rounded-t-[26px] p-[10px] pb-0"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    boxShadow:
                      'inset -0.5px -0.5px 2px 0px rgba(255, 123, 52, 0.3), inset 0.5px 0.5px 2px 0px rgba(255, 249, 238, 0.8), 0px 12px 25px 0px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <img
                    src={editorUiPreview}
                    alt="Creator Lab Preview"
                    className="h-auto w-[469px] rounded-t-[16px] object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="px-6">
              <Dialog.Description className="font-inter text-sm font-normal leading-5 tracking-normal text-spring-neutral-b1">
                {t('content-recording.download-dialog.description')}
              </Dialog.Description>
            </div>

            <div className="flex justify-end gap-2 px-6 pt-5">
              <button
                type="button"
                className="h-8 rounded-[.625rem] border border-white/20 bg-spring-neutral-base px-3 font-inter text-sm font-medium leading-5 text-spring-neutral-b0 transition-colors hover:bg-white/5"
                onClick={handleDownload}
                tabIndex={0}
                aria-label={t(
                  'content-recording.download-dialog.download-recording',
                )}
              >
                {t('content-recording.download-dialog.download-recording')}
              </button>
              <button
                type="button"
                className="h-8 rounded-[.625rem] bg-spring-primary px-3 font-inter text-sm font-medium leading-5 text-spring-neutral-b0 transition-colors hover:bg-spring-primary/90"
                onClick={handleGoToCreatorLab}
                tabIndex={0}
                aria-label={t(
                  'content-recording.download-dialog.go-to-creator-lab',
                )}
              >
                {t('content-recording.download-dialog.go-to-creator-lab')}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
